import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useAuth } from "../components/AuthProvider.js";
import { api } from "../lib/api.js";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button.js";

export const Route = createFileRoute("/guests/new")({ component: NewGuestPage });

function NewGuestPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ name: "", email: "", phone: "", title: "", company: "", bio: "", website: "", twitter: "", linkedin: "", youtube: "", tags: "" });

  if (!user) return <div>Loading...</div>;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post("/api/guests", {
        name: form.name, email: form.email, phone: form.phone || undefined,
        title: form.title || undefined, company: form.company || undefined, bio: form.bio || undefined,
        socialLinks: { website: form.website || undefined, twitter: form.twitter || undefined, linkedin: form.linkedin || undefined, youtube: form.youtube || undefined },
        tags: form.tags.split(",").map(t => t.trim()).filter(Boolean),
      });
      if (!res.ok) throw new Error("Failed");
      const data = await res.json();
      navigate({ to: "/guests/$guestId", params: { guestId: data.guest.id } });
    } catch {
      setError("Failed to create guest.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">Add New Guest</h1>
      {error && <div className="mb-4 p-3 bg-destructive/10 text-destructive rounded-md text-sm">{error}</div>}
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid md:grid-cols-2 gap-4">
          <div><label className="block text-sm font-medium mb-1">Name *</label><input type="text" required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="w-full px-3 py-2 border rounded-md" /></div>
          <div><label className="block text-sm font-medium mb-1">Email *</label><input type="email" required value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="w-full px-3 py-2 border rounded-md" /></div>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div><label className="block text-sm font-medium mb-1">Title</label><input type="text" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} className="w-full px-3 py-2 border rounded-md" /></div>
          <div><label className="block text-sm font-medium mb-1">Company</label><input type="text" value={form.company} onChange={e => setForm({ ...form, company: e.target.value })} className="w-full px-3 py-2 border rounded-md" /></div>
        </div>
        <div><label className="block text-sm font-medium mb-1">Bio</label><textarea value={form.bio} onChange={e => setForm({ ...form, bio: e.target.value })} className="w-full px-3 py-2 border rounded-md" rows={4} /></div>
        <div className="grid md:grid-cols-2 gap-4">
          <div><label className="block text-sm font-medium mb-1">Website</label><input type="url" value={form.website} onChange={e => setForm({ ...form, website: e.target.value })} className="w-full px-3 py-2 border rounded-md" /></div>
          <div><label className="block text-sm font-medium mb-1">Twitter</label><input type="text" value={form.twitter} onChange={e => setForm({ ...form, twitter: e.target.value })} className="w-full px-3 py-2 border rounded-md" /></div>
          <div><label className="block text-sm font-medium mb-1">LinkedIn</label><input type="text" value={form.linkedin} onChange={e => setForm({ ...form, linkedin: e.target.value })} className="w-full px-3 py-2 border rounded-md" /></div>
          <div><label className="block text-sm font-medium mb-1">YouTube</label><input type="text" value={form.youtube} onChange={e => setForm({ ...form, youtube: e.target.value })} className="w-full px-3 py-2 border rounded-md" /></div>
        </div>
        <div><label className="block text-sm font-medium mb-1">Tags</label><input type="text" value={form.tags} onChange={e => setForm({ ...form, tags: e.target.value })} className="w-full px-3 py-2 border rounded-md" placeholder="tag1, tag2, tag3" /></div>
        <div className="flex gap-3">
          <Button type="submit" disabled={loading} className="px-6">
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creating...
              </>
            ) : (
              "Create Guest"
            )}
          </Button>
          <Link to="/guests" className="px-6 py-2 border rounded-md font-medium hover:bg-muted inline-flex items-center">Cancel</Link>
        </div>
      </form>
    </div>
  );
}
